import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ticketService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const TicketDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const [ticketRes, commentsRes] = await Promise.all([
          ticketService.getById(id),
          ticketService.getComments(id, true)
        ]);
        setTicket(ticketRes.data.data);
        setComments(commentsRes.data.data);
      } catch (error) {
        console.error('Failed to fetch ticket:', error);
        toast.error('Failed to load ticket');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [id]);

  const onSubmitComment = async (data) => {
    setSubmitting(true);
    try {
      const response = await ticketService.addComment(id, {
        content: data.content,
        isInternal: data.isInternal || false
      });
      setComments([...comments, response.data.data]);
      reset();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner h-8 w-8"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900">Ticket not found</h2>
        <Link to="/tickets" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
          Back to tickets
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-1">
            <Link to="/tickets" className="hover:text-gray-700">Tickets</Link>
            <span>/</span>
            <span>{ticket.ticket_number}</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{ticket.subject}</h1>
        </div>
        <div className="flex items-center space-x-2">
          <span
            className="badge"
            style={{
              backgroundColor: `${ticket.status_color}20`,
              color: ticket.status_color
            }}
          >
            {ticket.status_name}
          </span>
          <span
            className="badge"
            style={{
              backgroundColor: `${ticket.priority_color}20`,
              color: ticket.priority_color
            }}
          >
            {ticket.priority_name}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-medium">Description</h2>
            </div>
            <div className="card-body">
              <p className="whitespace-pre-wrap text-gray-700">{ticket.description}</p>
            </div>
          </div>

          {/* Comments */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-medium">Comments ({comments.length})</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.id} className={`p-4 ${comment.is_internal ? 'bg-yellow-50' : ''}`}>
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0">
                        <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white text-sm font-medium">
                          {comment.first_name?.[0]}{comment.last_name?.[0]}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <p className="text-sm font-medium text-gray-900">
                            {comment.first_name} {comment.last_name}
                          </p>
                          {comment.is_internal && (
                            <span className="badge badge-yellow text-xs">Internal</span>
                          )}
                          <span className="text-sm text-gray-500">
                            {format(new Date(comment.created_at), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                          {comment.content}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  No comments yet
                </div>
              )}
            </div>

            {/* Add Comment Form */}
            <div className="card-body border-t">
              <form onSubmit={handleSubmit(onSubmitComment)} className="space-y-4">
                <div>
                  <textarea
                    rows={3}
                    placeholder="Add a comment..."
                    className={`input ${errors.content ? 'input-error' : ''}`}
                    {...register('content', { required: 'Comment is required' })}
                  />
                  {errors.content && (
                    <p className="mt-1 text-sm text-red-600">{errors.content.message}</p>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      {...register('isInternal')}
                    />
                    <span>Internal note (not visible to requester)</span>
                  </label>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary btn-sm"
                  >
                    {submitting ? 'Adding...' : 'Add Comment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Details */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-medium">Details</h2>
            </div>
            <div className="card-body space-y-4">
              <div>
                <p className="text-sm text-gray-500">Requester</p>
                <p className="font-medium">
                  {ticket.requester_first_name} {ticket.requester_last_name}
                </p>
                <p className="text-sm text-gray-500">{ticket.requester_email}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Assignee</p>
                <p className="font-medium">
                  {ticket.assignee_first_name
                    ? `${ticket.assignee_first_name} ${ticket.assignee_last_name}`
                    : 'Unassigned'}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Team</p>
                <p className="font-medium">{ticket.team_name || 'Not assigned'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Hotel</p>
                <p className="font-medium">{ticket.hotel_name || 'Not specified'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Category</p>
                <p className="font-medium">{ticket.category || 'Not categorized'}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {format(new Date(ticket.created_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Last Updated</p>
                <p className="font-medium">
                  {format(new Date(ticket.updated_at), 'MMM d, yyyy h:mm a')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketDetail;
